import { getCoins } from '../services/api';

// Cache for token symbols to avoid repeated API calls
const tokenSymbolCache = new Map();

/**
 * Get token symbol from address using the getCoins API
 * @param {string} dexName - The DEX name (e.g., 'pancakeswap')
 * @param {string} tokenAddress - The token contract address
 * @returns {Promise<string>} The token symbol or empty string if not found
 */
export const getTokenSymbol = async (dexName, tokenAddress) => {
  if (!dexName || !tokenAddress) {
    return '';
  }

  // Check cache first
  const cacheKey = `${dexName}:${tokenAddress.toLowerCase()}`;
  if (tokenSymbolCache.has(cacheKey)) {
    return tokenSymbolCache.get(cacheKey);
  }

  try {
    // Get all coins for the DEX
    const coinsData = await getCoins(dexName);
    
    if (coinsData && coinsData.coins) {
      // Find the token by address
      const token = coinsData.coins.find(
        coin => coin.address?.toLowerCase() === tokenAddress.toLowerCase()
      );
      
      if (token && token.symbol) {
        // Cache the result
        tokenSymbolCache.set(cacheKey, token.symbol);
        return token.symbol;
      }
    }
    
    // If not found, cache empty string to avoid repeated failed lookups
    tokenSymbolCache.set(cacheKey, '');
    return '';
  } catch (error) {
    console.warn(`Failed to lookup token symbol for ${tokenAddress} on ${dexName}:`, error);
    // Cache empty string to avoid repeated failed lookups
    tokenSymbolCache.set(cacheKey, '');
    return '';
  }
};

/**
 * Get token symbol from address with fallback to known tokens
 * @param {string} dexName - The DEX name
 * @param {string} tokenAddress - The token contract address
 * @param {string} fallbackSymbol - Fallback symbol if lookup fails
 * @returns {Promise<string>} The token symbol
 */
export const getTokenSymbolWithFallback = async (dexName, tokenAddress, fallbackSymbol = '') => {
  const symbol = await getTokenSymbol(dexName, tokenAddress);
  return symbol || fallbackSymbol;
};
